import React, { useMemo } from 'react';

import classNames from 'classnames';

import { Dialog, DialogButtonType, DialogProps } from '@ast/magma/components/dialog';

import { FlexContainer } from '@app/common/components/FlexContainer/FlexContainer';
import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import { useSubmitContact } from '@app/widgets/contacts-and-settings/hooks/useSubmitContact';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { AddressFormCache, AddressItemFields, AddressRecord } from '@app/widgets/contacts-and-settings/types';
import {
  addressLinesToHTML,
  addressRecordToUppercase,
  getAddressLines,
  highlightAddressDiff,
} from '@app/widgets/contacts-and-settings/utils/address';

import styles from './AddressSuggestionModal.pcss';

export type Addresses = {
  readonly providedAddress?: AddressRecord;
  readonly suggestedAddress?: Partial<AddressRecord>;
};

export type AddressSuggestionModalProps = DialogProps & Addresses & {
  readonly formCache: AddressFormCache | null;
  readonly successMessage?: string;
  readonly onSuccess?: () => void;
};

export const AddressSuggestionModal = ({
  providedAddress,
  suggestedAddress,
  formCache,
  successMessage,
  onSuccess,
  ...props
}: AddressSuggestionModalProps) => {
  const { t } = useUserSettingsTranslation();
  const isMobile = useIsMobile();

  const mergedSuggestedAddress = useMemo(() => (
    suggestedAddress && providedAddress
      ? addressRecordToUppercase({ ...providedAddress, ...suggestedAddress })
      : {} as AddressRecord
  ), [suggestedAddress, providedAddress]);

  const addressesCollection = useMemo(() => {
    const suggestedLines = mergedSuggestedAddress && getAddressLines(mergedSuggestedAddress);
    const providedLines = providedAddress && getAddressLines(providedAddress);

    return {
      provided: suggestedLines && providedLines
        ? highlightAddressDiff(providedLines, suggestedLines)
        : '',
      suggested: suggestedLines
        ? addressLinesToHTML(suggestedLines)
        : '',
    };
  }, [mergedSuggestedAddress, providedAddress]);

  const { isLoading, submitContact } = useSubmitContact({ successMessage, onSuccess });

  const handleSubmit = () => {
    submitContact({
      [AddressItemFields.AddressGroup]: {
        ...formCache?.[AddressItemFields.AddressGroup],
        ...mergedSuggestedAddress,
      },
    });
  };

  return (
    <Dialog
      {...props}
      className={styles.dialog}
      buttons={[{
        type: DialogButtonType.Cancel,
        label: t(
          'address-suggestion-dialog.cancel-button|Address suggestion dialog cancel button label',
          'Continue editing',
        ),
      }, {
        type: DialogButtonType.OK,
        label: t(
          'address-suggestion-dialog.confirm-button|Address suggestion dialog confirm button label',
          'Use recommended',
        ),
        onClick: () => {
          handleSubmit();
          return true;
        },
      }]}
      defaultButton={DialogButtonType.OK}
      align={isMobile ? 'bottom' : 'center'}
      predefinedWidth="md"
    >
      <FlexContainer
        gap="small"
        direction="column"
      >
        <header>
          <h3 className={styles.infoTitle}>
            {t(
              'address-suggestion-dialog.info.title|Address suggestions modal info title',
              'Please verify your address',
            )}
          </h3>
          <p className={styles.infoDetails}>
            {t(
              'address-suggestion-dialog.info.details|Address suggestions modal info details',
              'We\'ve detected potential issues with the address you\'ve provided, which are highlighted in red below. You may either accept our suggested address or edit your entry. This step ensures the accuracy of your address for mailing purposes.',
            )}
          </p>
        </header>
        <section>
          <div className={styles.addressList}>
            <dl className={styles.address}>
              <dt>
                {t(
                  'address-suggestion-dialog.provided-address.title|Title for provided address section',
                  'You provided',
                )}
              </dt>
              <dd
                data-stable-name="ProvidedAddress"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: addressesCollection.provided }}
              />
            </dl>
            <dl className={classNames(styles.address, styles.suggested)}>
              <dt>
                {t(
                  'address-suggestion-dialog.recommended-address.title|Title for recommended address section',
                  'Recommended',
                )}
              </dt>
              <dd
                data-stable-name="RecommendedAddress"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: addressesCollection.suggested }}
              />
            </dl>
          </div>
        </section>
      </FlexContainer>
      <OverlaySpinner
        visible={isLoading}
        containerClassName={styles.overlayContainer}
      />
    </Dialog>
  );
};
